import React, { useState, useCallback } from 'react'
import {
  Box,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Button,
  useDisclosure,
  Collapse,
  Icon,
  Tag,
  TagLabel,
  TagCloseButton,
  HStack,
  Heading,
  useColorModeValue,
  Text,
} from '@chakra-ui/react'
import { SearchIcon } from '@chakra-ui/icons'
import { FaSort } from 'react-icons/fa'
import { MdFilterList } from 'react-icons/md'

export function FilterBar({ onFilterChange }) {
  const { isOpen, onToggle } = useDisclosure()
  const [filters, setFilters] = useState({
    searchTerm: '',
    prioridade: '',
    concluido: '',
  })

  const bgColor = useColorModeValue('white', 'gray.700')
  const borderColor = useColorModeValue('gray.200', 'gray.600')

  const handleInputChange = useCallback(
    (e) => {
      const { name, value } = e.target

      setFilters((prev) => {
        const newFilters = { ...prev, [name]: value }
        onFilterChange(newFilters)
        return newFilters
      })
    },
    [onFilterChange]
  )

  const handleClearFilters = useCallback(() => {
    const resetFilters = {
      searchTerm: '',
      prioridade: '',
      concluido: '',
    }

    setFilters(resetFilters)
    onFilterChange(resetFilters)
  }, [onFilterChange])

  // Função para remover um filtro específico
  const removeFilter = (filterName) => {
    setFilters((prev) => {
      const newFilters = { ...prev, [filterName]: '' }
      onFilterChange(newFilters)
      return newFilters
    })
  }

  // Mapeamento dos valores de filtro para nomes amigáveis
  const priorityLabels = {
    baixa: 'Baixa',
    media: 'Média',
    alta: 'Alta',
    'muito-alta': 'Muito Alta',
  }

  const statusLabels = {
    S: 'Concluídos',
    N: 'Em aberto',
  }

  // Verifica se existem filtros ativos
  const hasActiveFilters = filters.prioridade || filters.concluido

  return (
    <Box bg={bgColor} p={4} borderRadius="md" shadow="sm" mb={4} borderWidth="1px" borderColor={borderColor}>
      <Flex justify="space-between" align="center" mb={4}>
        <Heading size="sm" mb={2}>
          Filtrar Chamados
        </Heading>

        <Button leftIcon={<Icon as={MdFilterList} />} onClick={onToggle} variant="outline" colorScheme="blue" size="sm">
          {isOpen ? 'Esconder filtros' : 'Mostrar filtros'}
        </Button>
      </Flex>

      <InputGroup size="md" mb={4}>
        <InputLeftElement pointerEvents="none">
          <SearchIcon color="gray.400" />
        </InputLeftElement>
        <Input
          placeholder="Buscar por número, tipo ou detalhes do chamado..."
          name="searchTerm"
          value={filters.searchTerm}
          onChange={handleInputChange}
          borderRadius="md"
          bg={useColorModeValue('white', 'gray.800')}
        />
      </InputGroup>

      <Collapse in={isOpen} animateOpacity>
        <Box py={2}>
          <Flex direction={{ base: 'column', md: 'row' }} gap={4} align={{ base: 'stretch', md: 'center' }} mb={4}>
            <Select
              placeholder="Filtrar por prioridade"
              name="prioridade"
              value={filters.prioridade}
              onChange={handleInputChange}
              icon={<FaSort />}
              variant="filled"
              bg={useColorModeValue('gray.50', 'gray.700')}
            >
              <option value="baixa">Prioridade: Baixa</option>
              <option value="media">Prioridade: Média</option>
              <option value="alta">Prioridade: Alta</option>
              <option value="muito-alta">Prioridade: Muito Alta</option>
            </Select>

            <Select
              placeholder="Status de conclusão"
              name="concluido"
              value={filters.concluido}
              onChange={handleInputChange}
              icon={<FaSort />}
              variant="filled"
              bg={useColorModeValue('gray.50', 'gray.700')}
            >
              <option value="S">Status: Concluídos</option>
              <option value="N">Status: Em aberto</option>
            </Select>

            <Button colorScheme="gray" onClick={handleClearFilters} size="md" isDisabled={!hasActiveFilters && !filters.searchTerm}>
              Limpar todos
            </Button>
          </Flex>

          {/* Mostra tags de filtros ativos */}
          {hasActiveFilters && (
            <Box mt={2}>
              <Text fontSize="sm" fontWeight="medium" mb={2}>
                Filtros ativos:
              </Text>
              <HStack spacing={2} flexWrap="wrap">
                {filters.prioridade && (
                  <Tag size="md" key="prioridade" borderRadius="full" variant="solid" colorScheme="blue">
                    <TagLabel>Prioridade: {priorityLabels[filters.prioridade]}</TagLabel>
                    <TagCloseButton onClick={() => removeFilter('prioridade')} />
                  </Tag>
                )}

                {filters.concluido && (
                  <Tag size="md" key="status" borderRadius="full" variant="solid" colorScheme={filters.concluido === 'S' ? 'green' : 'yellow'}>
                    <TagLabel>Status: {statusLabels[filters.concluido]}</TagLabel>
                    <TagCloseButton onClick={() => removeFilter('concluido')} />
                  </Tag>
                )}
              </HStack>
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  )
}
